import { useLocalization } from "@fluent/react";
import { useLocation, useNavigate } from "react-router-dom";
import { Table, MenuButton } from "@narmi/design_system";
import {
  DisplayStatus,
  showCancelOption,
} from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import {
  useDualApprovalPermission,
  useACHReverseCollectPermission,
  useACHReverseSendPermission,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments/permissions";
import {
  useIsDomesticWireDualApprovalRequired,
  useIsInternationalWireDualApprovalRequired,
  useIsACHDualApprovalRequired,
  useUser,
} from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import { FILTER_CONFIG, FilterChips } from "../Filters/filterConfig";
import { TableWithDrawer, RenderDrawerProps, PaymentActivityRow } from "../common";
import { PAYMENT_ACTIVITY_ROUTE, getReverseFromListRoute } from "../routes";
import { PaymentActivityDrawer } from "./";

const PaymentActivityTable = ({
  payments,
  handleToggleCancelDialog,
}: {
  payments: API.FormattedPaymentActivity[];
  handleToggleCancelDialog: (target: {
    id: string;
    model: string;
    usesDualApproval: boolean;
  }) => void;
}) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const hasDualApprovalPermission = useDualApprovalPermission();
  const hasReverseSendPermission = useACHReverseSendPermission();
  const hasReverseCollectPermission = useACHReverseCollectPermission();
  const isDomesticWireDualApprovalRequired =
    useIsDomesticWireDualApprovalRequired();
  const isInternationalWireDualApprovalRequired =
    useIsInternationalWireDualApprovalRequired();
  const isAchReversalDualApprovalRequired = useIsACHDualApprovalRequired();
  const currentUser = useUser();
  const institution = useInstitutionSettings();

  const handleOpenDrawer = (uuid: string) => {
    navigate(`/${PAYMENT_ACTIVITY_ROUTE}/${uuid}${location.search}`);
  };

  const renderRow = (item: API.FormattedPaymentActivity) => {
    const {
      uuid,
      displayStatus,
      dualApprovalId,
      isCollectPayment,
      isInternationalWirePayment,
      isFedWirePayment,
      isReversal,
    } = item;
    const isPendingApproval = displayStatus === DisplayStatus.pending_approval;
    const showCancelButton = showCancelOption(item, {
      currentUserUuid: currentUser?.id,
      isDomesticWireDualApprovalRequired,
      isInternationalWireDualApprovalRequired,
      isAchReversalDualApprovalRequired,
    });
    const menuItems = [];

    if (isPendingApproval && dualApprovalId && hasDualApprovalPermission) {
      menuItems.push(
        <MenuButton.Item
          key="view-button"
          label={l10n.getString("label-view-approvals")}
          startIcon="eye"
          onSelect={() =>
            window.location.assign(
              `/approvals/pending?dualApprovalId=${dualApprovalId}`
            )
          }
        />
      );
    }

    if (displayStatus === DisplayStatus.in_progress) {
      menuItems.push(
        <MenuButton.Item
          key="view-button"
          label={l10n.getString("label-view-recurring")}
          startIcon="eye"
          onSelect={() => {
            navigate({
              pathname: `/${PAYMENT_ACTIVITY_ROUTE}`,
              search: `?${
                FILTER_CONFIG[FilterChips.RECURRING_PAYMENT].queryKey
              }=${uuid}`,
            });
          }}
        />
      );
    }

    const hasReversePermission = isCollectPayment
      ? hasReverseCollectPermission
      : hasReverseSendPermission;
    const showReverseButton =
      institution?.features?.user_initiated_ach_reversals &&
      item.isReversible &&
      hasReversePermission;

    if (showReverseButton) {
      menuItems.push(
        <MenuButton.Item
          key="reverse-button"
          label={l10n.getString("label-reverse-payment")}
          startIcon="rotate-ccw"
          onSelect={() =>
            navigate(`${getReverseFromListRoute(uuid)}${location.search}`)
          }
        />
      );
    }

    if (showCancelButton) {
      menuItems.push(
        <MenuButton.Item
          key="delete-button"
          label={l10n.getString("label-cancel-payment")}
          startIcon="trash-2"
          onSelect={() => {
            const isWire = isFedWirePayment || isInternationalWirePayment;
            // wires and ACH reversals are cancelled via their dual approval,
            // so pass the dual approval id rather than the object uuid
            const usesDualApproval =
              (isWire || isReversal) && Boolean(dualApprovalId);
            handleToggleCancelDialog({
              id: usesDualApproval
                ? (dualApprovalId as string)
                : (uuid as string),
              model: item.model,
              usesDualApproval,
            });
          }}
        />
      );
    }

    return (
      <PaymentActivityRow
        key={`table-row__${uuid}`}
        payment={item}
        onRowClick={() => handleOpenDrawer(uuid)}
        actionCell={
          <Table.Cell>
            <div
              style={{ alignSelf: "end" }}
              onClick={(e) => e.stopPropagation()}
            >
              {menuItems.length > 0 && (
                <MenuButton triggerIcon="more-vertical">{menuItems}</MenuButton>
              )}
            </div>
          </Table.Cell>
        }
      />
    );
  };

  const renderDrawer: RenderDrawerProps<API.FormattedPaymentActivity> = (
    payment,
    paymentId,
    { onClose, onNext, onPrev, isOpen }
  ) => (
    <PaymentActivityDrawer
      isOpen={isOpen}
      handleClose={onClose}
      handleOnNext={onNext}
      handleOnPrev={onPrev}
      handleToggleCancelDialog={handleToggleCancelDialog}
      payment={payment}
      paymentUuid={paymentId as API.ScheduledACHPaymentId | API.ACHPaymentId}
    />
  );

  return (
    <TableWithDrawer
      items={payments}
      renderRow={renderRow}
      renderDrawer={renderDrawer}
      basePath={PAYMENT_ACTIVITY_ROUTE}
      paramKey="activityUuid"
    />
  );
};

export default PaymentActivityTable;
